
import useAttendee from "containers/attendee/useHooks";
import React, { Fragment, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ExplorerIcon from "assets/images/others/attendee-side-images/reward.png";
import InnovatorIcon from "assets/images/others/attendee-side-images/achievement.png";
import { LockSvg  } from "assets/images/svgs";
import {WhiteCircleSvg} from "assets/images/svgs";
import { useSelector } from "react-redux";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import AwardedBadge from "components/popups/road-map/AwardedBadge";
import CoreConceptModal from "components/popups/road-map/coreConcepts";
import CustomMessageDisplay from "components/data-not-found/CustomMessageDisplay";
import noRoadMapFound from "assets/images/gifs/roadmap-not-found.gif";


const Roadmap = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  const [assessmentCoreConceptsData, setAssessmentCoreConceptsData] =
    useState<any>(null);
  const [pathPoints, setPathPoints] = useState<{ x: number; y: number }[]>([]);
  const [isAwardedBadgeModalOpen, setIsAwardedBadgeModalOpen] = useState(false);
  const [isCoreConceptModalOpen, setIsCoreConceptModalOpen] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState<any>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const openAwardedBadgeModal = (badgeData: any) => {
    setSelectedBadge(badgeData);
    setIsAwardedBadgeModalOpen(true);
  };
  const closeAwardedBadgeModal = () => {
    setIsAwardedBadgeModalOpen(false);
    setSelectedBadge(null);
  };

  const openCoreConceptModal = () => setIsCoreConceptModalOpen(true);
  const closeCoreConceptModal = () => setIsCoreConceptModalOpen(false);
  const { getAssessmentCoreConceptsById } = useAttendee();

  useEffect(() => {
    if (id) {
      getAssessmentCoreConceptsById(Number(id), setAssessmentCoreConceptsData);
    }
  }, []);

  // Set initial load to false after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoad(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // Show all concepts with initialAverage < 3, regardless of explorer/innovator classification
  const allConcepts =
    assessmentCoreConceptsData?.coreConcepts.filter(
      (c: any) => c.initialAverage < 3
    ) || [];

  // Count concepts that have awardedBadge (not null)
  const unlockedConceptsCount = allConcepts.filter(
    (c: any) => c.awardedBadge !== null
  ).length;

  // Get badge data from concepts that have awardedBadge
  const badgeData = allConcepts
    .filter((c: any) => c.awardedBadge !== null)
    .map((c: any) => ({
      image: concatPathWithBackendUrl(c.awardedBadge.image.name),
      name: c.awardedBadge.name,
      conceptName: c.name,
      conceptDescription: c.description
    }));

  const totalPoints = allConcepts.length + 2; // +2 for explorer and innovator nodes 
  const marginLeft = 100; // Left margin in pixels
  const marginRight = 100; // Right margin in pixels

  const generatePathPoints = (count: number) => {
    const points: { x: number; y: number }[] = [];
    const zigzagWidth = 200; // Width of each zigzag segment
    const zigzagHeight = 100; // Height of each zigzag
    const totalWidth = (count - 1) * zigzagWidth; // Total width for proper zigzag
    const height = 300;
    const centerY = height * 0.5;

    if (count < 2) {
      points.push({ x: marginLeft + totalWidth * 0.5, y: centerY });
      return points;
    }

    for (let i = 0; i < count; i++) {
      const x = marginLeft + i * zigzagWidth;
      const isEven = i % 2 === 0;
      const y = isEven ? centerY - zigzagHeight / 2 : centerY + zigzagHeight / 2;

      points.push({ x, y });
    }
    return points;
  };

  useEffect(() => {
    if (assessmentCoreConceptsData?.coreConcepts?.length) {
      const total = totalPoints;
      setPathPoints(generatePathPoints(total));
    } else {
      setPathPoints(generatePathPoints(Math.max(2, totalPoints)));
    }
  }, [assessmentCoreConceptsData]);

  const getPoint = (index: number) => {
    if (pathPoints && pathPoints[index]) return pathPoints[index];
    const zigzagWidth = 200;
    const zigzagHeight = 100;
    const centerY = 150;
    const x = marginLeft + index * zigzagWidth;
    const isEven = index % 2 === 0;
    const y = isEven ? centerY - zigzagHeight / 2 : centerY + zigzagHeight / 2;
    return { x, y };
  };

  const explorerNodeIndex = 0;
  const innovatorNodeIndex = totalPoints - 1;

  // Show loading state for initial 3 seconds or while data is loading
  // if (isInitialLoad || isLoading) {
  //   return (
  //     <Fragment>
  //       <div className="flex items-center justify-center py-20">
  //         <div className="text-center">
  //           <img src={learnlocalLoader} alt="Loading..." className="w-20 h-20 mx-auto" />
  //         </div>
  //       </div>
  //     </Fragment>
  //   );
  // }

  // Check if there are no core concepts to show (after loading completes)
  if (!assessmentCoreConceptsData || !assessmentCoreConceptsData.coreConcepts || assessmentCoreConceptsData.coreConcepts.length === 0) {
    return (
      <Fragment>
        <CustomMessageDisplay
          show={true}
          title="No Record Found"
          slogan="No core concepts available for this assessment."
          image={noRoadMapFound}
          imageAlt="No core concepts found"
          className="py-20"
          imageClassName="w-[150px] h-[120px]"
          titleClassName="text-xl font-semibold text-gray-600"
          sloganClassName="text-gray-500"
        />
      </Fragment>
    );
  }

  return (
    <Fragment>
      {/* <div className="bg-primaryBlueBg w-[380px] h-[148px] flex items-center justify-between p-4 rounded-lg relative my-10">
        <div className="flex flex-col gap-2">
          <h1 className="text-white text-2xl font-medium">Builder Level</h1>
          <span className="text-white bg-[#415868] px-3 py-1 rounded-full text-sm">
            Follow road map to level up
          </span>
        </div>
        <div className="absolute -right-14 ">
          <div className="relative w-[171px] h-[171px] bg-white rounded-full p-2 custom-card-shadow">
            <svg
              className="size-full -rotate-90"
              viewBox="0 0 36 36"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx={18}
                cy={18}
                r={16}
                fill="none"
                className="stroke-current text-inputsBorderColor"
                strokeWidth={2.5}
              />
              <circle
                cx={18}
                cy={18}
                r={16}
                fill="none"
                className="stroke-current text-primaryBlueText"
                strokeWidth={2.5}
                strokeDasharray={100}
                strokeDashoffset={100 - 35}
              />
            </svg>
            <div className="absolute top-1/2 start-1/2 transform -translate-y-1/2 -translate-x-1/2">
              <span className="text-center text-5xl font-bold text-primaryBlueText">
                35%
              </span>
            </div>
          </div>
        </div>
      </div> */}


      <div className="w-full overflow-hidden">
        <div className="overflow-x-auto custom-scrollbar">
          <div
            className="relative min-h-[300px] mx-auto"
            style={{
              width: `${Math.max(800, (totalPoints - 1) * 200 + marginLeft + marginRight)}px`,
              minWidth: '800px'
            }}
          >
            <svg
              className="absolute inset-0 w-full h-full"
              viewBox={`0 0 ${(totalPoints - 1) * 200 + marginLeft + marginRight} 300`}
              preserveAspectRatio="xMidYMid meet"
            >
              <path
                d={createPath(pathPoints)}
                fill="none"
                stroke="#DAA520"
                strokeWidth="4"
                strokeDasharray="12 8"
                className="drop-shadow-sm"
              />
            </svg>

            {(() => {
              const p = getPoint(explorerNodeIndex);
              const totalWidth = (totalPoints - 1) * 200 + marginLeft + marginRight;
              return (
                <div
                  className="absolute z-40  text-center"
                  style={{
                    left: `${(p.x / totalWidth) * 100}%`,
                    top: `${(p.y / 300) * 100}%`,
                    transform: 'translate(-50%, -60%)',
                  }}
                >
                  <h2 className="text-gray-600 font-semibold text-lg mb-2">
                    Initial Node
                  </h2>
                  <div
                    className="w-20 h-20 bg-primaryBlueBg rounded-full flex items-center justify-center shadow-lg border-4 border-white mx-auto"
                  >
                    <img
                      src={ExplorerIcon}
                      alt="Explorer"
                      className="w-10 h-10"
                    />
                  </div>
                </div>
              );
            })()}

            {/* Display generic nodes based on count of concepts with initialAverage < 3 */}
            {Array.from({ length: allConcepts.length }, (_, index) => {
              const point = getPoint(index + 1);
              const totalWidth = (totalPoints - 1) * 200 + marginLeft + marginRight;
              return (
                <div
                  key={`node-${index}`}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 text-center group"
                  style={{
                    left: `${(point.x / totalWidth) * 100}%`,
                    top: `${(point.y / 300) * 100}%`,
                  }}
                >
                  <div className="w-12 h-12 bg-yellowLightBg rounded-full flex items-center justify-center border-2 border-white shadow-slate-300 shadow-lg node-active">
                    {index < unlockedConceptsCount ? (
                       <img
                         src={badgeData[index].image}
                         alt="badge"
                         className="w-8 h-8 object-contain cursor-pointer"
                         onClick={() => openAwardedBadgeModal(badgeData[index])}
                       />
                    ) : index === unlockedConceptsCount ? (
                      <WhiteCircleSvg 
                        onClick={openCoreConceptModal} 
                        className="w-6 h-6 cursor-pointer"
                      />
                    ) : (
                      <LockSvg className="w-6 h-6"/>
                    )}
                  </div>
                </div>
              );
            })}

            {(() => {
              const p = getPoint(innovatorNodeIndex);
              const totalWidth = (totalPoints - 1) * 200 + marginLeft + marginRight;
              return (
                <div
                  className="absolute text-center"
                  style={{
                    left: `${(p.x / totalWidth) * 100}%`,
                    top: `${(p.y / 300) * 100}%`,
                    transform: 'translate(-50%, -60%)',
                  }}
                >
                  <h2 className="text-gray-600 font-semibold text-lg mb-2 whitespace-nowrap">
                    Ending Node
                  </h2>
                  <div className="w-20 h-20 bg-primaryBlueBg rounded-full flex items-center justify-center shadow-lg border-4 border-white mx-auto">
                    <img
                      src={InnovatorIcon}
                      alt="Innovator"
                      className="w-10 h-10"
                    />
                  </div>
                </div>
              );
            })()}

          </div>
        </div>

      </div>


       {
         isAwardedBadgeModalOpen && (
           <AwardedBadge 
             isOpen={isAwardedBadgeModalOpen} 
             onClose={closeAwardedBadgeModal}
             badgeData={selectedBadge}
           />
         )
       }
      {
        isCoreConceptModalOpen && (
          <CoreConceptModal 
            isOpen={isCoreConceptModalOpen} 
            onClose={closeCoreConceptModal}
            coreConcepts={assessmentCoreConceptsData?.coreConcepts || []}
          />
        )
      }

    </Fragment>
  );
};

function createPath(points: { x: number; y: number }[]) {
  if (!points || points.length < 2) return "";
  let path = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    const curr = points[i];
    const prev = points[i - 1];
    const cp1x = prev.x + (curr.x - prev.x) * 0.5;
    const cp1y = prev.y;
    const cp2x = curr.x - (curr.x - prev.x) * 0.5;
    const cp2y = curr.y;
    path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${curr.x} ${curr.y}`;
  }
  return path;
}

export default Roadmap;
